import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import type { PedidoVista } from '@shared/types';
import { puedeUsarCanal } from '@shared/permisos';
import { MINUTOS_AVISO_CUENTA, minutosConCuenta } from '@shared/cuentaMesa';
import { useAjustesPermisos } from '@renderer/store/useAjustesPermisos';
import { useSessionStore } from '@renderer/store/useSessionStore';

/**
 * Sprint 168 — "La Mesa 1 tiene la cuenta entregada hace 20 min y no se ha cobrado."
 *
 * El 28/10 la Mesa 1 imprimió su cuenta a las 4:13 pm, la dueña se fue a Existencias y siguió vendiendo
 * en el mostrador: la mesa quedó abierta toda la tarde y nada se lo recordó (la tarjeta de pedidos
 * sin concluir sólo pregunta al día siguiente). Este aviso vive en el layout, como los de domicilio,
 * para verse en CUALQUIER pantalla en cuanto una cuenta lleva {@link MINUTOS_AVISO_CUENTA} minutos
 * entregada sin cobrar. No se cierra con una equis: se quita cobrando (o liberando la mesa).
 *
 * Sólo lo ve quien puede cobrar. En Mesas no aparece: ahí la tarjeta de la mesa ya lo dice en rojo.
 */
const CADA_MS = 60_000;

interface CuentaPendiente {
  pedidoId: number;
  folio: number;
  mesa: number | null;
  minutos: number;
}

export function CuentasSinCobrar() {
  const sesion = useSessionStore((s) => s.sesion);
  const ajustes = useAjustesPermisos((s) => s.ajustes);
  const puedeCobrar = puedeUsarCanal(sesion?.rol, 'pedidos:cobrar', ajustes);
  const location = useLocation();
  const navigate = useNavigate();
  const [pendientes, setPendientes] = useState<CuentaPendiente[]>([]);

  useEffect(() => {
    if (!puedeCobrar) return;
    let vivo = true;
    const revisar = async () => {
      try {
        const [p, m] = await Promise.all([
          window.api.pedidos.listarPorEstado(['abierto', 'pendiente_cocina', 'cocina', 'listo', 'entregado']),
          window.api.mesas.listar(),
        ]);
        if (!vivo) return;
        if (!p.ok) {
          setPendientes([]); // sesión vencida o sin permiso: no se queda pintado un aviso viejo
          return;
        }
        const numeroDeMesa = new Map(m.ok ? m.data.map((x) => [x.id, x.numero] as const) : []);
        const ahora = Date.now();
        const lista = (p.data as PedidoVista[])
          .filter((x) => x.cuentaSolicitada && x.mesaId != null)
          .map((x) => ({
            pedidoId: x.id,
            folio: x.numeroPedido,
            mesa: numeroDeMesa.get(x.mesaId as number) ?? null,
            minutos: minutosConCuenta(x.fechaCuentaSolicitada, ahora) ?? 0,
          }))
          .filter((x) => x.minutos >= MINUTOS_AVISO_CUENTA)
          .sort((a, b) => b.minutos - a.minutos);
        setPendientes(lista);
      } catch {
        /* sin red: se reintenta en la siguiente vuelta */
      }
    };
    void revisar();
    const t = setInterval(() => void revisar(), CADA_MS);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [puedeCobrar]);

  if (!puedeCobrar || pendientes.length === 0 || location.pathname.startsWith('/mesas')) return null;

  const primera = pendientes[0];
  const quien = primera.mesa != null ? `La Mesa ${primera.mesa}` : `El pedido #${String(primera.folio).padStart(5, '0')}`;
  const otras = pendientes.length - 1;

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border-2 border-rose-400 bg-rose-50 px-4 py-3"
      role="alert"
    >
      <Receipt className="h-6 w-6 shrink-0 text-rose-700" aria-hidden />
      <p className="flex-1 text-base text-rose-900">
        <strong>{quien}</strong> tiene la cuenta entregada hace {primera.minutos} min y{' '}
        <strong>no se ha cobrado</strong>.
        {otras > 0 && ` Y ${otras === 1 ? 'otra mesa' : `${otras} mesas más`} también.`}
      </p>
      <button
        type="button"
        onClick={() => navigate('/mesas')}
        className="min-h-[44px] rounded-lg bg-rose-700 px-5 font-bold text-white hover:bg-rose-800"
      >
        Ir a cobrar
      </button>
    </div>
  );
}
